// Sidebar callback badge. Shows overdue + due-in-15-min count, refetches on
// callback_changed, and pulses red when overdue > 0.

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CallbackCounts } from '@marnic/shared';
import { api } from '../lib/api';
import { getSocket } from '../lib/socket';

export function CallbackBadge(): JSX.Element | null {
  const qc = useQueryClient();
  const counts = useQuery({
    queryKey: ['callback-counts'],
    queryFn: () => api.get<CallbackCounts>('/callbacks/counts'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onChanged = (): void => {
      void qc.invalidateQueries({ queryKey: ['callback-counts'] });
    };
    const onDueSoon = (): void => {
      void qc.invalidateQueries({ queryKey: ['callback-counts'] });
    };
    socket.on('callback_changed', onChanged);
    socket.on('callback_due_soon', onDueSoon);
    return () => {
      socket.off('callback_changed', onChanged);
      socket.off('callback_due_soon', onDueSoon);
    };
  }, [qc]);

  const overdue = counts.data?.overdue ?? 0;
  const dueSoon = counts.data?.dueWithin15Min ?? 0;
  const total = overdue + dueSoon;
  if (total === 0) return null;

  return (
    <span
      className={`ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
        overdue > 0 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'
      }`}
      title={`${overdue} overdue, ${dueSoon} due within 15 min`}
    >
      {total}
    </span>
  );
}

// Pulsing dot for the softphone header — shown when overdue > 0.
export function CallbackOverdueDot(): JSX.Element | null {
  const counts = useQuery({
    queryKey: ['callback-counts'],
    queryFn: () => api.get<CallbackCounts>('/callbacks/counts'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  if (!counts.data || counts.data.overdue === 0) return null;
  return (
    <span
      className="relative inline-flex h-2.5 w-2.5"
      title={`${counts.data.overdue} overdue callback${counts.data.overdue === 1 ? '' : 's'}`}
    >
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
    </span>
  );
}
